import { useState, useCallback } from 'react'
import { useStore } from 'react-redux'
import type { RootState } from '@/store/store'
import { useAppDispatch } from '@/store/store-hooks'
import { UserFoldersActions } from '../store/slice'
import { createFolder } from '../fetch/post-create-folder'
import { FolderResponse } from '../schemas'
import type { FolderPathPrecondition } from '@/user_files/schemas/preconditions'
import { getUserFilesErrorMessage, isUserFilesNameConflict, shouldRefreshAfterUserFilesError } from '@/user_files/utils/api-error'
import { buildFolderPathPrecondition } from '@/user_files/utils/preconditions'
import { cacheResolvedFolders } from '@/user_files/folders/utils/cache-resolved-folders'

interface UseCreateFolderOptions {
  onSuccess?: (folder: FolderResponse) => void
  onError?: (error: Error) => void
}

interface UseCreateFolderReturn {
  createFolder: (parent: string | Pick<FolderResponse, 'id' | 'path'>, name: string) => Promise<FolderResponse | null>
  isLoading: boolean
  error: string | null
  reset: () => void
}

function getParentFolderRequest(parent: string | Pick<FolderResponse, 'id' | 'path'>): { parentId: string; precondition?: FolderPathPrecondition } {
  if (typeof parent === 'string') return { parentId: parent }
  return {
    parentId: parent.id,
    precondition: buildFolderPathPrecondition(parent),
  }
}

/**
 * useCreateFolder
 *
 * Hook for creating folders with local state management.
 * Calls the API directly and updates Redux on success.
 *
 * Unlike the listener-based approach, this provides:
 * - Proper async/await flow for the caller
 * - Local loading/error state
 * - Callbacks for success/error handling
 */
export function useCreateFolder(options: UseCreateFolderOptions = {}): UseCreateFolderReturn {
  const { onSuccess, onError } = options
  const dispatch = useAppDispatch()
  const store = useStore<RootState>()

  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const reset = useCallback(() => {
    setError(null)
    setIsLoading(false)
  }, [])

  const create = useCallback(
    async (parent: string | Pick<FolderResponse, 'id' | 'path'>, name: string): Promise<FolderResponse | null> => {
      const { parentId, precondition } = getParentFolderRequest(parent)

      setIsLoading(true)
      setError(null)

      try {
        const request = precondition ? { parent_id: parentId, name, parent_folder_precondition: precondition } : { parent_id: parentId, name }
        const folder = await createFolder(request)

        // On the folder the drive has open the refetch below is what introduces the row, in the sort
        // in force; caching it here could only put it at the bottom until that listing arrives. The
        // move dialog creates folders in the destination it is browsing, which nothing else
        // refetches, so there the row is cached straight away.
        cacheResolvedFolders({ dispatch, getState: store.getState, parentId }, [folder])
        dispatch(UserFoldersActions.fetchFolderChildren({ folderId: parentId }))

        setIsLoading(false)
        onSuccess?.(folder)
        return folder
      } catch (err) {
        const errorMessage = getUserFilesErrorMessage(err, 'Failed to create folder')
        if (shouldRefreshAfterUserFilesError(err) || !isUserFilesNameConflict(err)) {
          dispatch(UserFoldersActions.fetchFolderChildren({ folderId: parentId }))
        }
        setError(errorMessage)
        setIsLoading(false)
        onError?.(err instanceof Error ? err : new Error(errorMessage))
        return null
      }
    },
    [dispatch, store, onSuccess, onError]
  )

  return {
    createFolder: create,
    isLoading,
    error,
    reset,
  }
}
